/* global google */
import { useEffect, useState, useCallback } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useCurrentBreakpointName } from "react-socks";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MapRangeStepper from "./MapRangeStepper";

import { BreakpointsSize } from "../../../utils/constants";
import i18n from "../../../i18n/i18n";

import locationPinImage from "../../../images/mapSearch/location-pin.png";
import locationPinHighlightImage from "../../../images/mapSearch/location-pin-highlight.png";
import { useNavigate } from "react-router-dom";
import { Routes } from "../../../utils/routes";

const Map = ({
  defaultLocation,
  parkingAreas,
  userLocation,
  selectedParkingAreaId,
  hoveringParkingAreaId,
  onHoverPin,
  onClickPin,
}) => {
  const [mapCenter, setMapCenter] = useState(defaultLocation);
  const [showRedoSearchButton, setShowRedoSearchButton] = useState(false);
  const [map, setMap] = useState(null);
  const breakpoint = useCurrentBreakpointName();
  const navigate = useNavigate();

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    if (userLocation) {
      setMapCenter(userLocation);
    }
  }, [userLocation]);

  const handleMapDrag = () => {
    const newMapCenter = map.getCenter();
    // Calculate distance between original user location and current map center
    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(mapCenter.lat, mapCenter.lng),
      newMapCenter
    );

    // If the distance is greater than 1km, recenter the map
    if (distance < 500) {
      setShowRedoSearchButton(false);
      setMapCenter(mapCenter);
    } else {
      setShowRedoSearchButton(true);
      setMapCenter({
        lat: newMapCenter.lat(),
        lng: newMapCenter.lng(),
      });
    }
  };

  const setSearchedLocationCoordinates = () => {
    navigate(`${Routes.FindParking}?lat=${mapCenter.lat}&lng=${mapCenter.lng}`);
  };

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  return (
    isLoaded && (
      <>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{ gestureHandling: "greedy" }}
          center={mapCenter}
          zoom={17}
          onDragEnd={handleMapDrag}
        >
          {showRedoSearchButton && (
            <div className="absolute top-0 left-1/2">
              <div className="flex justify-center">
                <button
                  type="button"
                  className="h-10 w-[170px] absolute top-[65px] flex flex-row text-sm items-center justify-center bg-primary900 shadow text-white px-4 rounded-full"
                  onClick={() => setSearchedLocationCoordinates(mapCenter)}
                >
                  <FontAwesomeIcon icon={faSearch} className="mr-1" />
                  {i18n.t("findParking.redoSearchHere")}
                </button>
              </div>
            </div>
          )}
          <Marker position={mapCenter} />
          {parkingAreas &&
            parkingAreas.map((place, index) => (
              <Marker
                onMouseOver={() => onHoverPin(place.id)}
                icon={
                  (
                    breakpoint === BreakpointsSize.SMALL
                      ? selectedParkingAreaId === place.id
                      : hoveringParkingAreaId === place.id
                  )
                    ? {
                        url: locationPinHighlightImage,
                        scaledSize: new google.maps.Size(90, 45),
                        anchor: new google.maps.Point(24, 50),
                        labelOrigin: new google.maps.Point(59, 25),
                      }
                    : {
                        url: locationPinImage,
                        scaledSize: new google.maps.Size(66, 38),
                        anchor: new google.maps.Point(20, 34),
                        labelOrigin: new google.maps.Point(45, 25),
                      }
                }
                label={{
                  text:
                    "" +
                    (place.displayMonthlyPrice
                      ? parseFloat(place.displayMonthlyPrice.avg / 100).toFixed(
                          0
                        )
                      : "-"),
                  fontFamily: "Arial Black, Gadget, sans-serif",
                  fontSize: (
                    breakpoint === BreakpointsSize.SMALL
                      ? selectedParkingAreaId === place.id
                      : hoveringParkingAreaId === place.id
                  )
                    ? "16px"
                    : "12px",
                  color: "white",
                }}
                key={place.id}
                position={{
                  lat: Number(place.location.latitude),
                  lng: Number(place.location.longitude),
                }}
                zIndex={
                  (
                    breakpoint === BreakpointsSize.SMALL
                      ? selectedParkingAreaId === place.id
                      : hoveringParkingAreaId === place.id
                  )
                    ? google.maps.Marker.MAX_ZINDEX
                    : index
                }
                onClick={() => onClickPin(place)}
                clickable
                visible
              ></Marker>
            ))}
          <MapRangeStepper />
        </GoogleMap>
      </>
    )
  );
};

export default Map;
